<template>
  <div
    v-click-outside="closeItems"
    class="pdca-container"
    :class="elClasses"
    @click.stop="showPdcaItems"
  >
    <div class="pdca-container__current-step pdca-step" :class="pdcaStepClasses(value)">
      <img :src="require(`@/assets/img/pdca-${currentStep.ref}.svg`)" alt="">
      <div class="pdca-container__current-step__text">
        {{ currentStep.text.toLowerCase() }}
      </div>
    </div>

    <TransitionExpand>
      <div
        v-if="displayItems"
        class="pdca-container__items"
        :class="{ 'pdca-container__items--auto-width': quickAction }"
      >
        <div
          v-for="(item, idx) in workflowSteps"
          :key="idx"
          class="pdca-container__items__row"
        >
          <div class="v-centerer f-w pdca-step" :class="pdcaStepClasses(idx)" @click="changePDCA(idx)">
            <img :src="require(`@/assets/img/pdca-${item.ref}.svg`)" alt="">
            <div :style="`color: ${item.color};`">{{ item.text }}</div>
          </div>
          <Icon v-if="idx === 0" class="pdca-container__close-arrow" @click="displayItems = false">
            {{ arrowDownLine }}
          </Icon>
        </div>
      </div>
    </TransitionExpand>
  </div>
</template>

<script>
/**
 * PDCA wheel component
 *
 * @vue-prop {Number} [value=0] - PDCA value, can be bind with 'v-model'
 * @vue-prop {Number} [pdcaId] - ID of the action whose PDCA is changed - will send server request on change
 * @vue-prop {Boolean} [disabled=false] - Disable PDCA value edition by displaying an editor around the chart
 *
 * @vue-event {Number} input - Emit new PDCA value selected
 */
import toastr from 'toastr';
import Icon from '@/components/icon.vue';
import TransitionExpand from '@/components/transition-expand.vue';
// Vuex
import { EDIT_ACTION } from '@/store/modules/problem/action-types';
// Mixins
import mixinSocketHandler from '@/mixins/socket-handler.vue';
// Utils
import SocketMessages from '@/utils/classes/SocketMessages';
import { arrowDownLine } from '@/assets/icons';
import { getActionWorkflow, getActionWorkflowStep } from '@/utils/action-utils';

export default {

  name: 'Pdca',

  components: {
    Icon,
    TransitionExpand,
  },

  mixins: [mixinSocketHandler],

  props: {
    value: {
      type: Number,
      default: 0,
      validator(value) {
        return value >= 0 && value <= 4;
      },
    },
    pdcaId: {
      type: Number,
      required: true,
    },
    disabled: Boolean,
    quickAction: Boolean,
  },

  data() {
    return {
      arrowDownLine,
      displayItems: false,
    };
  },

  computed: {
    /** Array: Available steps in the workflow */
    workflowSteps() {
      return getActionWorkflow({ isQuickAction: this.quickAction });
    },
    currentStep() {
      return this.workflowSteps[Number(this.value).sBetweenBoundaries(0, this.workflowSteps.length - 1)];
    },
    /** Object: Classes applied to this.$el */
    elClasses() {
      return {
        'pdca-container--opened': this.displayItems,
        'pdca-container--disabled': this.disabled,
      };
    },
  },

  methods: {
    changePDCA(newPDCAValue) {
      if (this.pdcaId && this.displayItems) {
        this.$store.dispatch(`problem/${EDIT_ACTION}`, { id: this.pdcaId, pdca: newPDCAValue }).then(({ data }) => {
          if (data instanceof Object) {
            this.$emit('input', newPDCAValue);
            this.sendSocketMessage(SocketMessages.CC_MSGS.ACTION_PLANS);
          } else {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
          }
        });
      } else {
        this.$emit('input', newPDCAValue);
      }
    },
    showPdcaItems() {
      if (!this.disabled) {
        this.displayItems = !this.displayItems;
      } else {
        this.displayItems = false;
      }
    },
    closeItems() {
      this.displayItems = false;
    },
    /** Object: Classes applied to a PDCA step */
    pdcaStepClasses(step) {
      return {
        [`pdca-step--${getActionWorkflowStep({ isQuickAction: this.quickAction, pdca: step }).ref}`]: true,
      };
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.pdca-container {
  position: relative;
  cursor: pointer;
  font-size: 0.9rem;

  img {
    margin-right: 0.3rem;
    flex: 0 0 .8rem;
    display: block;
    width: 0.8rem;
    height: 0.8rem;
  }

  .pdca-step {
    box-sizing: border-box;
    padding: 0.4rem;
    display: flex;
    align-items: center;
    border-radius: $small-border-radius;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    background: $color-gray-background;
    font-variant: small-caps;

    &--plan {
      color: $color-pdca-plan;
      background: $color-pdca-plan-medium;

      &:hover {
        background: $color-pdca-plan-medium-hover;
      }
    }

    &--do {
      color: $color-pdca-do;
      background: $color-pdca-do-medium;

      &:hover {
        background: $color-pdca-do-medium-hover;
      }
    }

    &--check {
      color: $color-pdca-check;
      background: $color-pdca-check-medium;

      &:hover {
        background: $color-pdca-check-medium-hover;
      }
    }

    &--act {
      color: $color-pdca-act;
      background: $color-pdca-act-medium;

      &:hover {
        background: $color-pdca-act-medium-hover;
      }
    }
  }

  &__current-step {
    flex: 1;

    &__text {
      font-weight: normal;
      font-variant: small-caps;
      // Since there's no uppercase letter, reduce line-heightto better align text and icon
      line-height: 1rem;
      transform: translateY(-0.5px);
    }
  }

  &--disabled {
    cursor: initial;

    .pdca-container__current-step {
      margin-right: 0.5rem;
    }
  }

  &__items {
    position: absolute;
    background-color: white;
    border-radius: 4px;
    padding: 4px;
    left: 0;
    top: 0;
    width: 80px;
    z-index: 1;
    box-shadow: $light-box-shadow;

    &--auto-width {
      width: auto;
    }

    &__row {
      display: flex;
      align-items: center;

      &:not(:last-child) {
        margin-bottom: 0.4rem;
      }

      .pdca-step {
        padding-right: 1.7rem;
      }
    }
  }

  &__close-arrow {
    flex: 0 0 0.7rem;
    margin-right: 0.5rem;
    height: 1rem;
  }

  &__close-arrow {
    transform: rotate(180deg);
    position: absolute;
    top: .9rem;
    right: .4rem;
    width: 0.7rem;
    height: 0.7rem;
  }
}
</style>
